import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Upload,
  CheckCircle2,
  XCircle,
  Workflow,
  User,
  Shield,
  Sparkles,
  FileText,
  AlertTriangle,
  Activity,
  FileSearch,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface AuditEntry {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  description: string | null;
  createdAt: string;
  user: { firstName: string; lastName: string; email: string; role: string } | null;
}

const ACTION_META: Record<string, { icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  DOCUMENT_UPLOADED: { icon: Upload, tone: 'bg-blue-500/10 text-blue-600' },
  DOCUMENT_DELETED: { icon: XCircle, tone: 'bg-destructive/10 text-destructive' },
  TEXT_EXTRACTED: { icon: FileText, tone: 'bg-emerald-500/10 text-emerald-600' },
  WORKFLOW_STARTED: { icon: Workflow, tone: 'bg-indigo-500/10 text-indigo-600' },
  WORKFLOW_COMPLETED: { icon: CheckCircle2, tone: 'bg-emerald-500/10 text-emerald-600' },
  WORKFLOW_APPROVED: { icon: CheckCircle2, tone: 'bg-emerald-500/10 text-emerald-600' },
  WORKFLOW_REJECTED: { icon: XCircle, tone: 'bg-destructive/10 text-destructive' },
  WORKFLOW_FAILED: { icon: AlertTriangle, tone: 'bg-amber-500/10 text-amber-600' },
  USER_LOGGED_IN: { icon: User, tone: 'bg-muted text-muted-foreground' },
  USER_LOGGED_OUT: { icon: User, tone: 'bg-muted text-muted-foreground' },
  USER_CREATED: { icon: User, tone: 'bg-blue-500/10 text-blue-600' },
  AI_MODEL_CALLED: { icon: Sparkles, tone: 'bg-violet-500/10 text-violet-600' },
  AI_OUTPUT_REVIEWED: { icon: FileSearch, tone: 'bg-indigo-500/10 text-indigo-600' },
  COMPLIANCE_SCAN_STARTED: { icon: Shield, tone: 'bg-teal-500/10 text-teal-600' },
  PV_CASE_CREATED: { icon: Activity, tone: 'bg-rose-500/10 text-rose-600' },
};

export function ActivityFeed({ limit = 10 }: { limit?: number }) {
  const audit = useQuery({
    queryKey: ['audit', 'recent', limit],
    queryFn: async () => (await api.get(`/api/audit?pageSize=${limit}`)).data,
    refetchInterval: 15000,
  });

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-primary" /> Recent activity
        </CardTitle>
        <Link
          to="/audit"
          className="text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          View audit trail &gt;
        </Link>
      </CardHeader>
      <CardContent className="p-0">
        {audit.isLoading ? (
          <div className="space-y-3 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : !audit.data?.items?.length ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No activity yet.</div>
        ) : (
          <ol className="relative space-y-1 px-3 pb-3">
            {audit.data.items.map((entry: AuditEntry, idx: number) => {
              const meta = ACTION_META[entry.action] ?? { icon: Activity, tone: 'bg-muted text-muted-foreground' };
              const Icon = meta.icon;
              const isLast = idx === audit.data.items.length - 1;
              return (
                <li key={entry.id} className="relative flex gap-3 py-2">
                  {/* Timeline connector */}
                  {!isLast && (
                    <div className="absolute left-[15px] top-8 h-full w-px bg-border" aria-hidden />
                  )}
                  <div
                    className={cn(
                      'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      meta.tone,
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <span className="font-medium">
                        {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'System'}
                      </span>{' '}
                      <span className="text-muted-foreground">{humanizeAction(entry.action)}</span>
                    </div>
                    {entry.description && (
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {entry.description}
                      </div>
                    )}
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {formatRelative(entry.createdAt)}
                      {entry.user?.role && <span className="ml-2 uppercase tracking-wider">{entry.user.role}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function humanizeAction(action: string): string {
  const special: Record<string, string> = {
    DOCUMENT_UPLOADED: 'uploaded a document',
    DOCUMENT_PROCESSED: 'processed a document',
    DOCUMENT_DELETED: 'deleted a document',
    TEXT_EXTRACTED: 'extracted text',
    WORKFLOW_STARTED: 'started a workflow',
    WORKFLOW_COMPLETED: 'completed a workflow',
    WORKFLOW_APPROVED: 'approved an extraction',
    WORKFLOW_REJECTED: 'rejected an extraction',
    WORKFLOW_FAILED: 'workflow failed',
    USER_LOGIN: 'logged in',
    USER_LOGGED_IN: 'signed in',
    USER_LOGGED_OUT: 'signed out',
    USER_CREATED: 'was invited',
    AI_MODEL_CALLED: 'used an AI model',
    AI_OUTPUT_GENERATED: 'generated AI output',
    AI_OUTPUT_REVIEWED: 'reviewed an extraction',
    COMPLIANCE_SCAN_STARTED: 'started a compliance scan',
    PV_CASE_CREATED: 'created a case',
    PV_CASE_CODED: 'coded a case',
    PV_CASE_REVIEWED: 'reviewed a case',
    PV_CASE_NARRATED: 'drafted a narrative',
    PV_CASE_SUBMITTED: 'submitted a case',
    PV_CASE_CLOSED: 'closed a case',
    EXPORT_GENERATED: 'generated an export',
    EXTRACTION_APPROVED: 'approved an extraction',
    EXTRACTION_CORRECTED: 'corrected an extraction',
    EXTRACTION_REJECTED: 'rejected an extraction',
    TENANT_PROVISIONED: 'created a tenant',
    ESIGNATURE_APPLIED: 'applied an e-signature',
  };
  return special[action] ?? action.toLowerCase().replace(/_/g, ' ');
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
